/**
 * Proactive per-tier audio size check (spec §2.2 `plannedInputs`, decision 90,
 * 2026-08-19). `beginCreation`/`submitUserInput` reject an oversize file with
 * `invalid-argument`, but that costs a full round-trip and the callable's
 * generic `{ field }` shape (spec §1.1) gives no reliable "this was a size
 * rejection" signal to key a specific message off of. So the client mirrors
 * the caps and checks locally, before a doomed session is even queued.
 *
 * These numbers are a COPY, not a source of truth — the backend is the real
 * enforcer, and this repo has no shared contract with it beyond the spec doc.
 * Keep in sync by hand. Last synced: decision 90 (anonymous 5→20 MiB,
 * unverified 10→22 MiB; verified/paid unchanged at 25 MiB — the old
 * anonymous/unverified caps didn't fit a full 20-minute recording at
 * realistic voice-encoder bitrates).
 */
import type { AccountTier } from '../../types/backend';

const MIB = 1024 * 1024;

// Paid tiers (spec §14.1, decision 155): `plus | pro | max` all keep the
// 25 MiB storage-rules ceiling — tiers differ by counts, not bytes. Legacy
// `paid` is read as `max` server-side.
const TIER_CAP_BYTES = {
  anonymous: 20 * MIB,
  unverified: 22 * MIB,
  verified: 25 * MIB,
  plus: 25 * MIB,
  pro: 25 * MIB,
  max: 25 * MIB,
  paid: 25 * MIB,
} as const;

type KnownTier = keyof typeof TIER_CAP_BYTES;

function isKnownTier(tier: AccountTier | null): tier is KnownTier {
  return tier !== null && Object.hasOwn(TIER_CAP_BYTES, tier);
}

export interface AudioSizeCheck {
  /** False only when the tier is known AND the file exceeds its cap. */
  readonly ok: boolean;
  /** The cap that applied, in bytes. `null` when the tier was unknown and no
   *  check could be made. */
  readonly capBytes: number | null;
}

/**
 * Check `sizeBytes` against the cap for `tier`.
 *
 * An unresolved/unrecognized tier (the claim hasn't loaded yet, or a future
 * tier string this client doesn't know about) always passes — a false local
 * block is worse than letting the backend be the one to say no. See
 * CLAUDE.md's "fail safe" doctrine (Log Tab § "Answers, triggers, and the
 * kept recording" makes the same call for over/under-declared inputs).
 */
export function checkAudioSize(sizeBytes: number, tier: AccountTier | null): AudioSizeCheck {
  if (!isKnownTier(tier)) return { ok: true, capBytes: null };
  const capBytes = TIER_CAP_BYTES[tier];
  return { ok: sizeBytes <= capBytes, capBytes };
}

/** `1234567` → `"1.2"`, for a "X MiB" message. */
export function formatMiB(bytes: number): string {
  return (bytes / MIB).toFixed(1);
}
